import { onBeforeUnmount, ref } from 'vue'

/**
 * A reactive `window.matchMedia`.
 *
 * The design system switches layout in CSS wherever it can (`lg:hidden` and friends). This is for
 * the cases where the *markup* has to change too, not just the styling - a control that may not
 * merely be hidden on a narrow screen but has to stop being rendered in that state at all.
 *
 * Pass the same width the Tailwind breakpoint uses, so the JS and the CSS flip together:
 * `lg` is 64rem, `md` 48rem, `sm` 40rem (Tailwind v4 defaults; this app does not override them).
 *
 * @param {string} query A media query, e.g. `'(width < 64rem)'`.
 * @returns {import('vue').Ref<boolean>} Whether the query currently matches.
 */
export const useMediaQuery = (query) => {
  const matches = ref(false)

  // Guard for SSR and for the unit tests, which render components without a full window.
  if (
    typeof window === 'undefined' ||
    typeof window.matchMedia !== 'function'
  ) {
    return matches
  }

  const list = window.matchMedia(query)
  matches.value = list.matches

  const onChange = (event) => {
    matches.value = event.matches
  }

  list.addEventListener('change', onChange)
  onBeforeUnmount(() => list.removeEventListener('change', onChange))

  return matches
}

/**
 * Narrower than the `lg` breakpoint (1024px), i.e. phones and portrait tablets.
 *
 * `lg` is the line the app shell already uses: below it the section-nav column is hidden and the
 * section tabs take over, and the sidebar's own collapse toggle is `lg:flex`, so there is no
 * control to undo an expanded sidebar below it.
 *
 * @returns {import('vue').Ref<boolean>}
 */
export const useIsBelowDesktop = () => useMediaQuery('(width < 64rem)')
